<template>
  <ListComponent title="Trailer" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            class="search-field"
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchTrailers"
          ></v-text-field>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>

          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="8"
          class="px-0 pt-0 pb-2 d-flex justify-end"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 172px)"
            class="ag-theme-alpine"
            id="adminTrailerList"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :grid-options="gridOptions"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allTrailerList"
          />
        </v-col>
      </v-row>

      <AdminTrailerFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />
      <DetailsParent :resizable="true" v-model="detailsDrawer">
        <template #details>
          <AdminTrailerDetails
            :trailerId="selectedTrailerId"
            :permissions="permissions"
          />
        </template>
      </DetailsParent>
    </template>

    <template #dialogs>
      <BulkUpload
        v-model="trailerBulkUploadDialog"
        :uploadTo="uploadType"
        @refreshList="itemsPerPageChanged(20)"
      />
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        color="primary"
        depressed
        @click="addTrailer"
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Trailer</span>
      </v-btn>
      <v-btn
        class="ml-3"
        v-if="permissions && permissions.add"
        @click="openTrailerBulkUpload('trailer')"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Bulk Upload</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import Columns from "@/components/General/Columns.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import TrailerButton from "@/components/AgGridComponents/TrailerButton.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import AdminTrailerFilters from "@/components/ModuleBased/dialogs/filters/AdminTrailerFilters.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import AdminTrailerDetails from "@/components/ModuleBased/details/AdminTrailerDetails.vue";
import TripGoToButton from "@/components/AgGridComponents/TripGoToButton.vue";
import BulkUpload from "@/components/General/BulkUpload.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    TrailerButton,
    TripGoToButton,
    agColumnHeader: CustomHeader,
    AdminTrailerFilters,
    DetailsParent,
    AdminTrailerDetails,
    BulkUpload,
  },

  data() {
    return {
      sorting: {},
      headers: [
        {
          headerName: "Fleet Number",
          field: "inventory_number",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Trailer ID",
          field: "equipment_id",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Depot",
          field: "depot",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Trailer Number",
          field: "trailer_number",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Trailer Type",
          field: "trailer_type_title",
          sortable: true,
          tooltipField: "trailer_type_title",
          minWidth: 200,
        },
        {
          headerName: "Payload Capacity(TON)",
          field: "payload_capacity",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Road Trail",
          field: "road_trail",
          sortable: true,
          minWidth: 130,
        },

        {
          headerName: "Active Trip",
          field: "active_trip.trip_id",
          cellRendererFramework: "TripGoToButton",
          minWidth: 130,
        },
        {
          headerName: "Created By",
          field: "created_by",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "TrailerButton",
          pinned: "right",
          minWidth: 100,
        },
      ],

      allTrailerList: [],

      // Details vars
      selectedTrailerId: null,
      detailsDrawer: false,
      permissions: null,

      // pagination vars
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,

      // Filter vars
      filters: {},
      showFilterDialog: false,

      // bulk create
      uploadType: "driver",
      trailerBulkUploadDialog: false,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        defaultColDef: {
          resizable: true,
        },
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },

  methods: {
    openTrailerBulkUpload(type) {
      this.uploadType = type;
      this.trailerBulkUploadDialog = true;
    },
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    checkFilters() {
      const filters = localStorage.getItem("trailer_filters");
      const user_data = JSON.parse(localStorage.getItem("userProfile"));

      if (filters) {
        this.filters = JSON.parse(filters);
        if (this.filters.sorting) {
          this.sorting = { ...this.filters.sorting };
          this.headers.map((obj) => {
            if (obj.field in this.sorting) {
              if (this.sorting[obj.field] == false) {
                obj.sort = "desc";
              } else if (this.sorting[obj.field]) {
                obj.sort = "asc";
              }
            }
          });
        }
      }
    },
    setFilters() {
      if (
        this.filters &&
        Object.keys(this.filters).length > 0 &&
        Object.keys(this.filters).indexOf("ordering")
      ) {
        delete this.filters.ordering;
      }
      localStorage.setItem(
        "trailer_filters",
        JSON.stringify({ ...this.filters, sorting: this.sorting })
      );
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchTrailers() {
      this.pageNo = 1;
      this.getTrailerList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getTrailerList();
    },
    prevPage() {
      this.pageNo--;
      this.getTrailerList();
    },
    nextPage() {
      this.pageNo++;
      this.getTrailerList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    getTrailerList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        company: this.currentProfile.company,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.setFilters();
      this.$api.trailer
        .getTrailerList(params)
        .then((res) => {
          this.allTrailerList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    addTrailer() {
      this.$router.push("/app/admin/asset-management/trailer/add");
    },

    editTrailer(id) {
      this.$router.push(`/app/admin/asset-management/trailer/edit/${id}`);
    },

    goToTrip(trip_id) {
      this.$router.push({
        path: `/app/admin/trip/details/${trip_id}`,
        query: {
          backRoute: "/app/admin/asset-management/trailer/list",
        },
      });
    },

    viewTrailer(id) {
      this.selectedTrailerId = id;
      this.detailsDrawer = true;
    },
  },

  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("trailer");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.checkFilters();
        this.getTrailerList();
      }
    }
  },
};
</script>
